import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';

/**
 * Hooks for the Loans pages.
 *
 * Receipts (wire confirmations, check copies, ACH stubs) live in the
 * private `loan-receipts` Supabase Storage bucket. The bucket + RLS
 * are defined in `supabase/migrations/58_loan_receipts_storage.sql`
 * — only super_admin in the caller's org can read or write objects.
 *
 * Loan terms and payment-record metadata (the "paid on" date + the
 * receipt pointer) live in the browser today, keyed in localStorage
 * by `LoanLedger`. The blob itself is too large for localStorage so
 * we route file storage to Supabase even while the surrounding
 * metadata is still client-side. When a `loans` / `loan_payments`
 * table lands, the receipt pointer columns should move there and
 * these mutations should also write to the row.
 */

const LOAN_RECEIPTS_BUCKET = 'loan-receipts';

/** Caps an upload at 10 MB. A typical wire / ACH receipt PDF is well
 *  under 1 MB; the cap is here to prevent a user from accidentally
 *  uploading a multi-hundred-meg scan and burning Storage egress. */
export const MAX_LOAN_RECEIPT_BYTES = 10 * 1024 * 1024;

/**
 * Build the canonical receipt object key for a loan payment:
 *   `<organization_id>/<loan_slug>/p<payment_number>/<timestamp>-<filename>`
 *
 * The RLS policy in migration 58 only decodes segment 1
 * (`<organization_id>`); the remainder is operator-facing structure
 * so a glance at the Storage browser tells you which loan + payment
 * each receipt belongs to.
 */
export function buildLoanReceiptPath(args: {
  organizationId: string;
  loanSlug: string;
  paymentNumber: number;
  filename: string;
}): string {
  const safeSlug = args.loanSlug.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
  const safeFilename = sanitizeFilename(args.filename);
  // Zero-pad to 3 digits so the Storage browser sorts payment 12 ahead
  // of payment 100 visually. A 120-month loan never exceeds 3 digits.
  const paymentSegment = `p${String(args.paymentNumber).padStart(3, '0')}`;
  return `${args.organizationId}/${safeSlug}/${paymentSegment}/${safeFilename}`;
}

function sanitizeFilename(name: string): string {
  // Prepend a timestamp so re-uploads under the same payment + name
  // don't collide and the most recent file sorts last in the Storage
  // browser. Replace anything outside a safe S3-style charset with '_'.
  const stamp = Date.now();
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${stamp}-${cleaned}`;
}

export interface LoanReceiptRef {
  /** Full storage object key — pass back to mutate/remove. */
  path: string;
  /** Original filename the operator picked (no timestamp prefix). */
  filename: string;
  /** ISO timestamp of when this file was uploaded. */
  uploadedAt: string;
  /** Bytes — handy for the row's tooltip. */
  size: number;
  /** MIME type as reported by the browser at upload time. */
  contentType: string | null;
}

/**
 * Upload a loan-payment receipt. Replaces any prior receipt for the
 * same `(slug, paymentNumber)` if `previousPath` is provided — we
 * intentionally keep at most one file per payment to keep the UI
 * simple. The previous-file delete is best-effort so a successful
 * upload never pairs with a "couldn't delete the old file" failure.
 */
export function useUploadLoanReceipt() {
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      loanSlug: string;
      paymentNumber: number;
      file: File;
      previousPath: string | null;
    }): Promise<LoanReceiptRef> => {
      if (!profile?.organization_id) {
        throw new Error('Not signed in.');
      }
      if (input.file.size > MAX_LOAN_RECEIPT_BYTES) {
        throw new Error(
          `Receipt is ${(input.file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${MAX_LOAN_RECEIPT_BYTES / 1024 / 1024} MB.`,
        );
      }

      const path = buildLoanReceiptPath({
        organizationId: profile.organization_id,
        loanSlug: input.loanSlug,
        paymentNumber: input.paymentNumber,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(LOAN_RECEIPTS_BUCKET)
        .upload(path, input.file, {
          upsert: true,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      // Best-effort cleanup of the prior receipt. We swallow a failure
      // here so a successful upload never pairs with a "stale-file
      // delete" toast — the bucket will accumulate at worst one extra
      // orphan per re-upload, which a periodic Storage sweep can
      // address later.
      if (input.previousPath && input.previousPath !== path) {
        await supabase.storage
          .from(LOAN_RECEIPTS_BUCKET)
          .remove([input.previousPath]);
      }

      return {
        path,
        filename: input.file.name,
        uploadedAt: new Date().toISOString(),
        size: input.file.size,
        contentType: input.file.type || null,
      };
    },
  });
}

/** Remove the current receipt for a payment. */
export function useRemoveLoanReceipt() {
  return useMutation({
    mutationFn: async (input: { path: string }) => {
      const { error } = await supabase.storage
        .from(LOAN_RECEIPTS_BUCKET)
        .remove([input.path]);
      if (error) throw error;
    },
  });
}

/**
 * Mint a short-lived signed URL for a receipt so the browser can
 * open the PDF / image inline. Returned as a one-shot mutation
 * (rather than a query) because the consumer wants to navigate to
 * the URL the instant the user clicks "View" — caching is more
 * trouble than it's worth at the staleness window we'd need.
 */
export function useLoanReceiptSignedUrl() {
  return useMutation({
    mutationFn: async (input: { path: string }): Promise<string> => {
      const { data, error } = await supabase.storage
        .from(LOAN_RECEIPTS_BUCKET)
        .createSignedUrl(input.path, 60 * 5);
      if (error) throw error;
      if (!data?.signedUrl) throw new Error('No signed URL returned.');
      return data.signedUrl;
    },
  });
}
